import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { Country, CountryQuery, CountryService, MemberDetails, MemberQuery, MemberService, Region } from '@fgb/core';
import { tap } from 'rxjs/operators';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { AlertPreferencesComponent } from '../alert-preferences/alert-preferences.component';

@Component({
  selector: 'fgb-edit-account-details',
  templateUrl: './edit-account-details.component.html',
  styleUrls: ['./edit-account-details.component.scss'],
})
export class EditAccountDetailsComponent implements OnInit {
  member$: Observable<MemberDetails | undefined>;
  isInEditMode = false;
  portalId = '';
  detailsForm: UntypedFormGroup;
  isLoading = false;
  @Output() actionDone = new EventEmitter<boolean>();
  @Input() autoEditMode = false;
  maxYear: number;
  currentDate: number;
  currentMonth: number;
  baseData: any;

  /** Countries available for the Country field; each carries the Regions used to populate County. */
  countryOptions: Country[] = [];
  /** Regions for the currently selected country, shown as the County dropdown's options. */
  regionOptions: Region[] = [];
  /** County value to restore if it's still valid for whatever country ends up selected. */
  private selectedCounty = '';

  @ViewChild(AlertPreferencesComponent) alertPreferencesComponent: AlertPreferencesComponent;

  constructor(
    private memberQuery: MemberQuery,
    private memberService: MemberService,
    private formBuilder: UntypedFormBuilder,
    private countryQuery: CountryQuery,
    private countryService: CountryService
  ) {}

  ngOnInit() {
    this.member$ = this.memberQuery.selectMemberDetails().pipe(
      tap((z) => {
        if (z) {
          this.portalId = z.PortalId;
          const dob = this._getNgbDateFromString(z.DOB.toString());
          this.detailsForm = this.formBuilder.group({
            addressName: [z.AddressName],
            emailAddress: [z.EmailAddress1, Validators.email],
            dateOfBirth: [{ value: dob, disabled: !this.autoEditMode }],
            street: [z.Street],
            town: [z.Town],
            county: [z.County],
            country: [z.Country],
            postcode: [z.PostCode],
            firstName: [z.FirstName],
            surname: [z.Surname],
          });
          this.baseData = { ...this.detailsForm.value, dateOfBirth: dob };
          this.loadCountries(z.Country, z.County);
        }
      })
    );

    this.defaultDatePickerSettings();
  }

  /** Fetches the country list once, then seeds the County options for whatever country is already saved. */
  async loadCountries(currentCountry: string, currentCounty: string): Promise<void> {
    await this.countryService.fetchCountries().toPromise();
    this.countryOptions = this.countryQuery.getAll().sort((a, b) => {
      if (a.Ordinal < b.Ordinal) {
        return -1;
      } else if (a.Ordinal === b.Ordinal) {
        return a.DisplayName.localeCompare(b.DisplayName);
      }
      return 0;
    });
    this.selectedCounty = currentCounty;
    this.updateCountyOptionsForCountry(currentCountry);
  }

  /** Bound to the Country select's (change) event. */
  onCountryChange(countryName: string): void {
    this.selectedCounty = this.detailsForm.controls.county.value;
    this.updateCountyOptionsForCountry(countryName);
  }

  get selectedCountryDisplayName(): string {
    return this.countryOptions.find((c) => c.CountryName === this.detailsForm.controls.country.value)?.DisplayName ?? '';
  }

  selectCountry(country: Country): void {
    this.detailsForm.controls.country.setValue(country.CountryName);
    this.onCountryChange(country.CountryName);
  }

  /**
   * Repopulates the County options for the given country. The County dropdown is only offered for the
   * United States — every other country falls back to a free-text County field, left untouched since
   * free text has no "valid option" to check against.
   */
  private updateCountyOptionsForCountry(countryName: string): void {
    const foundCountry = this.countryOptions.find((c) => c.CountryName === countryName);
    const isUnitedStates = foundCountry?.DisplayName?.trim().toLowerCase() === 'united states';

    if (!isUnitedStates) {
      this.regionOptions = [];
      this.detailsForm.controls.county.setValue(this.selectedCounty);
      return;
    }

    this.regionOptions = foundCountry?.Regions ?? [];
    const countyStillValid = this.regionOptions.some((region) => region.RegionName === this.selectedCounty);
    this.detailsForm.controls.county.setValue(countyStillValid ? this.selectedCounty : '');
  }

  edit() {
    this.isInEditMode = true;
    this.detailsForm.get('dateOfBirth')?.enable();
  }

  stopEditing() {
    this.isInEditMode = false;
    this.detailsForm.patchValue(this.baseData);
    this.selectedCounty = this.baseData.county;
    this.updateCountyOptionsForCountry(this.baseData.country);
    this.detailsForm.get('dateOfBirth')?.disable();
    this.actionDone.emit(false);
  }

  notInAnyEditMode() {
    return !this.isInEditMode && !this.autoEditMode;
  }

  anyEditMode() {
    return this.isInEditMode || this.autoEditMode;
  }

  disableIfFormIncomplete(): string {
    const formComplete: Boolean =
      this.detailsForm.controls.street.value !== '' &&
      this.detailsForm.controls.emailAddress.value !== '' &&
      this.detailsForm.controls.dateOfBirth.value !== null;
    const formValid: Boolean = this.detailsForm.valid;
    if (formComplete && formValid) {
      return '';
    } else {
      return 'disabled';
    }
  }

  save() {
    this.isLoading = true;
    let memberDetails = {
      PortalId: this.portalId,
      EmailAddress1: this.detailsForm.controls.emailAddress.value,
      AddressName: this.detailsForm.controls.addressName.value,
      DOB: this._getStringFromNgbDate(this.detailsForm.controls.dateOfBirth.value),
      Street: this.detailsForm.controls.street.value,
      Town: this.detailsForm.controls.town.value,
      Country: this.detailsForm.controls.country.value,
      County: this.detailsForm.controls.county.value,
      PostCode: this.detailsForm.controls.postcode.value,
      FirstName: this.detailsForm.controls.firstName.value,
      Surname: this.detailsForm.controls.surname.value,
    } as MemberDetails;

    this.memberService.updateMemberDetails(this.portalId, memberDetails);

    this.alertPreferencesComponent.saveAlertPreferences();

    this.isInEditMode = false;
    this.isLoading = false;
    this.actionDone.emit(true);
  }

  /** Gets a date string in ISO format from an NgbDate. */
  private _getStringFromNgbDate(ngbDate: NgbDate) {
    if (ngbDate) {
      let date = new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day, 12);
      date.setUTCHours(0);
      date.setUTCMinutes(0);
      date.setUTCSeconds(0);
      return date.toISOString();
    } else {
      return '';
    }
  }

  /** Gets an NgbDate from a date string. */
  private _getNgbDateFromString(dateString: string): NgbDate | null {
    if (dateString) {
      const date = new Date(dateString);
      if (date) {
        return new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
      }
    }
    return null;
  }

  /* this should be configured according
   * to minimum age set by club to be
   * able to sign up to portal.
   */
  defaultDatePickerSettings() {
    this.maxYear = new Date().getFullYear() - 16;
    this.currentDate = new Date().getDate();
    this.currentMonth = new Date().getMonth() + 1;
  }
}
